import React, { useEffect, useState } from 'react';
import classnames from 'classnames/bind';
import { Slide as SlideAnimation } from 'react-awesome-reveal';
import { HeaderV6 } from '../HeaderV6';
import { Button } from '../Button';
import { QuizProgressBarV3 } from '../QuizProgressBarV3';
import { QuestionAnswerSlideV4 } from '../QuestionAnswerSlideV4';
import { StartSlideV5 } from '../StartSlideV5';
import { PauseSlide } from '../PauseSlide';
import { PlanLoaderSlideV3 } from '../PlanLoaderSlideV3';
import { EmailSlideV4 } from '../EmailSlideV4';
import { ProfileSlide } from '../ProfileSlide';
import { ProductSlide } from '../ProductSlide';
import { PlanGraphSlideV4 } from '../PlanGraphSlideV4';
import { SaleV29 } from '../SaleV29';
import { TimerV2 } from '../TimerV2';
import { FooterV2 } from '../FooterV2';
import { AgreementTextV3 } from '../AgreementTextV3';
import { useQuiz } from '../../shared/use-quiz';
import { useUserProperties } from '../../shared/use-user-properties';
import { useLegalInfo } from '../../shared/use-legal-info';
import { useSlideEventsV2 } from '../../shared/use-slide-events-v2';
import { isExpiredPromo } from '../../shared/promo-helpers';
import { getAnswersFromSource } from '../../shared/answer-helpers';
import {
    EMAIL_SLIDE,
    PAUSE_SLIDE,
    PLAN_GRAPH_SLIDE,
    PLAN_LOADER_SLIDE,
    PRODUCT_SLIDE,
    PROFILE_SLIDE,
    QUESTION_ANSWER_SLIDE_COMPONENT,
    START_SLIDE
} from '../../shared/quiz-component-types';
import { preloadImages } from '../../../lib/shared/preload-images';
import footerLogoSrc from '../../assets/images/boundly-logo-pink-light.png';
import emojiSpriteSrc from '../../assets/images/emoji-sprites/emoji-sprite-v51.png';
import pauseCollageUnderratingSrc from '../../assets/images/slide/pause-collage-underrating.png';
import pauseCollageOrdinarySrc from '../../assets/images/slide/pause-collage-ordinary.png';
import pauseCollageQuestionSrc from '../../assets/images/slide/pause-collage-question.png';
import pauseCollageAlbumSrc from '../../assets/images/slide/pause-collage-album.png';
import planGraphSrc from '../../assets/images/slide/plan-graph-six-months.png';
import styles from './CommonPrelanderV42.css';
import '../../assets/images/emoji-sprites/emoji-sprite-v51.css';

const cx = classnames.bind(styles);

const IMAGES = [emojiSpriteSrc, pauseCollageUnderratingSrc, pauseCollageOrdinarySrc, pauseCollageQuestionSrc, pauseCollageAlbumSrc, planGraphSrc];

const SLIDE_COMPONENTS = {
    [QUESTION_ANSWER_SLIDE_COMPONENT]: QuestionAnswerSlideV4,
    [START_SLIDE]: StartSlideV5,
    [PAUSE_SLIDE]: PauseSlide,
    [PLAN_LOADER_SLIDE]: PlanLoaderSlideV3,
    [EMAIL_SLIDE]: EmailSlideV4,
    [PROFILE_SLIDE]: ProfileSlide,
    [PRODUCT_SLIDE]: ProductSlide,
    [PLAN_GRAPH_SLIDE]: PlanGraphSlideV4
};

const DELAY = 350;

function CommonPrelanderV42({
    initialPrelander,
    quizSlides
}) {
    const {
        currentSlide,
        currentSlideIndex,
        results,
        submitAnswer,
        submitTemporaryAnswer,
        temporaryAnswer,
        finished,
        nextSlide,
        backSlide,
        setEmail,
        setExpiryTimestampPromo
    } = useQuiz(quizSlides, initialPrelander);
    const quizSlidesIndexing = quizSlides.filter(slide => !slide.isNotIndexing);
    const isQuestionSlide = currentSlide.component === QUESTION_ANSWER_SLIDE_COMPONENT;
    const isStartSlide = currentSlide.component === START_SLIDE;
    const isEmailSlide = currentSlide.component === EMAIL_SLIDE;
    const numberOfAnsweredSlides = results.userResponses.length + (isQuestionSlide ? 1 : 0);
    const [isExpired, setIsExpired] = useState(isExpiredPromo(results.expiryTimestampPromo));
    const { legalLinks, legalEntity } = useLegalInfo();
    const [selectedSubscriptionData, selectSubscription] = useState({});
    const [merchantData, setMerchantData] = useState({});

    useUserProperties(initialPrelander);

    useEffect(() => {
        preloadImages(IMAGES);
    }, []);

    const { logQuizLifecycle } = useSlideEventsV2({ currentSlideIndex: numberOfAnsweredSlides, results, currentSlide, quizSlides });

    const handleAnswerWithTransition = answer => () => {
        logQuizLifecycle(quizSlidesIndexing.length === numberOfAnsweredSlides);
        submitAnswer(answer);
    };

    const setExpiryTimestampDiscount = () => {
        setExpiryTimestampPromo();
        setIsExpired(false);
    };

    const handleContinue = () => {
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });
        logQuizLifecycle(quizSlidesIndexing.length === numberOfAnsweredSlides);

        if (currentSlideIndex === quizSlides.length - 1) {
            setExpiryTimestampDiscount();
        }

        nextSlide(temporaryAnswer);
    };

    const renderLeftNavigation = () => {
        if (finished) {
            if (!results.email || selectedSubscriptionData.plan) {
                return null;
            }

            return (
                <>
                    <TimerV2 expiryTimestamp={isExpired ? null : results.expiryTimestampPromo} label='Discount is reserved for:' />
                    <Button
                        label='Get my plan'
                        type='accent'
                        className={cx('buttonMyPlan', 'animation')}
                        onClick={() => document.getElementById('subscription-start-block')?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
                    />
                </>
            );
        }

        return null;
    };

    const renderHeader = () => {
        if (merchantData?.merchant) {
            return null;
        }

        const hasBackButton = !finished && Boolean(currentSlideIndex) && !currentSlide?.isNotBackButton;
        const hasPromoHeader = finished && Boolean(results.email);
        const isCategorizedQuestionSlide = isQuestionSlide && Boolean(currentSlide.props?.category);

        return (
            <HeaderV6
                renderLeftNavigation={renderLeftNavigation}
                progressBar={isCategorizedQuestionSlide && (
                    <QuizProgressBarV3
                        currentSlideIndex={numberOfAnsweredSlides}
                        slides={quizSlidesIndexing}
                    />
                )}
                questionNav={isCategorizedQuestionSlide && {
                    category: currentSlide.props?.category
                }}
                hasBackButton={hasBackButton}
                onBack={backSlide}
                className={cx('header', {
                    withBorder: finished,
                    withoutPromo: hasPromoHeader && isExpired,
                    withPromo: hasPromoHeader && !isExpired,
                    withPlan: Boolean(selectedSubscriptionData.plan)
                })}
                isFinished={finished && Boolean(results.email) && !selectedSubscriptionData.plan}
                url={window.location.pathname}
            />
        );
    };

    const renderFooter = () => {
        if (finished) {
            if (Boolean(results.email) && !selectedSubscriptionData.plan) {
                return <FooterV2 legalEntity={legalEntity} legalLinks={legalLinks} logoSrc={footerLogoSrc} />;
            }

            return null;
        }

        if (Boolean(currentSlide.continueButton)) {
            return (
                <SlideAnimation
                    delay={500}
                    duration={250}
                    triggerOnce
                    direction='up'
                    className={styles.slideFooter}
                    key={currentSlide.id}
                >
                    <div className={styles.slideFooterWrapper}>
                        <Button
                            type='accent'
                            label={currentSlide.continueButton.label || 'Continue'}
                            onClick={handleContinue}
                            inSlide
                        />
                    </div>
                </SlideAnimation>
            );
        }

        return null;
    };

    const renderContent = () => {
        if (finished) {
            return (
                <SaleV29
                    data={results}
                    expiryTimestamp={results.expiryTimestampPromo}
                    onExpirePromo={() => setIsExpired(true)}
                    isExpired={isExpired}
                    initialPrelander={initialPrelander}
                    selectSubscription={selectSubscription}
                    selectedSubscriptionData={selectedSubscriptionData}
                    merchantData={merchantData}
                    setMerchantData={setMerchantData}
                    legalLinks={legalLinks}
                />
            );
        }

        const Slide = SLIDE_COMPONENTS[currentSlide.component];
        const resultData = (temporaryAnswer?.id || temporaryAnswer?.answerIds)
            ? temporaryAnswer
            : results.userResponses.find(userResponse => userResponse.questionId === currentSlide.id);
        const dynamicAnswers = getAnswersFromSource(currentSlide, results);

        return (
            <div
                key={currentSlideIndex}
                className={cx('slide', {
                    withQuestion: isQuestionSlide,
                    withStartSlide: isStartSlide,
                    withEmailSlide: isEmailSlide,
                    withPauseSlide: currentSlide.component === PAUSE_SLIDE,
                    withPlanLoaderSlide: currentSlide.component === PLAN_LOADER_SLIDE
                })}
            >
                <Slide
                    {...currentSlide.props}
                    answers={dynamicAnswers || currentSlide.props?.answers}
                    spriteVersion='v51'
                    submitAnswer={handleAnswerWithTransition}
                    submitTemporaryAnswer={submitTemporaryAnswer}
                    userResponse={resultData}
                    onContinue={handleContinue}
                    id={currentSlide.id}
                    results={results}
                    delay={DELAY}
                    setEmail={setEmail}
                    legalLinks={legalLinks}
                />
                {isStartSlide && <AgreementTextV3 legalLinks={legalLinks} />}
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <div className={cx({ stickyHeader: finished && Boolean(results.email) && !selectedSubscriptionData.plan })}>
                {renderHeader()}
            </div>
            <main
                key={finished}
                className={cx('main', {
                    centered: currentSlide.centered,
                    animated: finished || isEmailSlide
                })}
            >
                {renderContent()}
            </main>
            {renderFooter()}
        </div>
    );
}

export {
    CommonPrelanderV42
};
